import React, { useState, useMemo, useRef, useEffect } from "react";
import { C, mono, font } from "../config/theme";

/* ═══════════════════════════════════════════════════════════════════════════════
   DOCUMENTATION DES 135 KPIs — 6 dashboards BatiViz

   Schéma de chaque entrée :
   - nom         : libellé court
   - description : ce que mesure le KPI
   - calcul      : formule en langage simple (pas SQL)
   - source      : tables bv_* utilisées

   Les colonnes "filtres" et "config" de l'ancienne version sont retirées :
   les filtres sont identiques à ceux de la page, et la config est désormais
   dans la page Paramètres (table bv_kpi_rules).
   ═══════════════════════════════════════════════════════════════════════════════ */

const DASHBOARDS = [
  /* ─────────────────────────────────────────────────────────────────────────────
     CA — 18 KPIs
     ───────────────────────────────────────────────────────────────────────────── */
  {
    id: "ca",
    label: "Chiffre d'affaires",
    color: "#F59E0B",
    kpis: [
      { nom: "CA facturé HT (période)",
        description: "Chiffre d'affaires facturé sur la période sélectionnée, hors taxes. Les avoirs sont déduits.",
        calcul: "Somme des montants HT des factures clients sur la période. Les avoirs (type_piece = 'avoir') comptent en négatif.",
        source: "bv_facture_client" },
      { nom: "CA cumulé exercice",
        description: "Cumul du CA facturé depuis le début de l'exercice comptable jusqu'à la fin de la période.",
        calcul: "Somme des montants HT des factures depuis le 1er jour de l'exercice. Les avoirs sont déduits.",
        source: "bv_facture_client, bv_kpi_rules (exercice_debut_mois)" },
      { nom: "Reste à facturer (RAF)",
        description: "Montant restant à facturer pour atteindre le total des marchés signés.",
        calcul: "Somme des marchés signés moins CA déjà facturé sur ces chantiers. Formule paramétrable.",
        source: "bv_marche, bv_facture_client, bv_kpi_rules (formule_raf)" },
      { nom: "Avancement moyen",
        description: "Avancement moyen pondéré de la facturation par rapport aux marchés signés.",
        calcul: "(CA cumulé ÷ Marché total) × 100, pondéré par la taille de chaque chantier.",
        source: "bv_facture_client, bv_marche" },
      { nom: "Nombre de chantiers facturés",
        description: "Nombre de chantiers distincts ayant au moins une facture sur la période.",
        calcul: "Comptage des chantier_id uniques dans les factures (hors avoirs).",
        source: "bv_facture_client" },
      { nom: "Panier moyen par chantier",
        description: "Montant moyen facturé par chantier sur la période.",
        calcul: "CA facturé période ÷ Nombre de chantiers facturés.",
        source: "bv_facture_client" },
      { nom: "CA moyen par situation",
        description: "Montant moyen d'une situation de travaux (type_piece = 'situation').",
        calcul: "Somme des montants des situations ÷ Nombre de situations.",
        source: "bv_facture_client (filtré type_piece = 'situation')" },
      { nom: "Nombre d'avoirs",
        description: "Nombre d'avoirs émis sur la période.",
        calcul: "Comptage des lignes avec type_piece = 'avoir'.",
        source: "bv_facture_client" },
      { nom: "Montant total des avoirs",
        description: "Somme négative des avoirs émis sur la période.",
        calcul: "Somme des montants HT où type_piece = 'avoir'.",
        source: "bv_facture_client" },
      { nom: "Évolution CA mensuelle N vs N-1",
        description: "Comparaison du CA mois par mois entre l'année courante et l'année précédente.",
        calcul: "Agrégation mensuelle du CA HT pour les exercices N et N-1, présenté en barres juxtaposées.",
        source: "bv_facture_client" },
      { nom: "Répartition CA par type de pièce",
        description: "Ventilation du CA entre situations, DGD, factures directes et avoirs (donut).",
        calcul: "Somme des montants HT groupée par type_piece, calcul du pourcentage de chaque type.",
        source: "bv_facture_client" },
      { nom: "CA cumulé vs marché total",
        description: "Courbe de la facturation cumulée comparée à l'objectif marché total (signature).",
        calcul: "CA cumulé mois par mois, comparé à la ligne plate du marché total signé.",
        source: "bv_facture_client, bv_marche" },
      { nom: "Top 10 clients",
        description: "Les 10 clients générant le plus de CA sur la période avec leur poids relatif.",
        calcul: "Agrégation du CA par client_id (bv_tiers), tri décroissant, top 10. Pourcentage = part du CA total.",
        source: "bv_facture_client, bv_chantier, bv_tiers" },
      { nom: "Top 10 chantiers facturés",
        description: "Les 10 chantiers générant le plus de CA sur la période.",
        calcul: "Agrégation du CA par chantier_id, tri décroissant, top 10.",
        source: "bv_facture_client, bv_chantier" },
      { nom: "CA par chargé d'affaires",
        description: "Performance commerciale individuelle (CA + nombre de factures + chantiers).",
        calcul: "Agrégation du CA par charge_affaires_id du chantier. Inclut nb factures et nb chantiers.",
        source: "bv_facture_client, bv_chantier, bv_collaborateur" },
      { nom: "CA par conducteur de travaux",
        description: "Performance opérationnelle individuelle (CA + nombre de factures + chantiers).",
        calcul: "Agrégation du CA par conducteur_id du chantier.",
        source: "bv_facture_client, bv_chantier, bv_collaborateur" },
      { nom: "CA par activité",
        description: "Ventilation du CA par activité métier (Électricité, Plomberie, CVC, etc.).",
        calcul: "Agrégation du CA par activite_id du chantier.",
        source: "bv_facture_client, bv_chantier, bv_activite" },
      { nom: "CA par agence",
        description: "Ventilation du CA par agence (Lyon, Grenoble, Saint-Étienne…).",
        calcul: "Agrégation du CA par agence_id du chantier.",
        source: "bv_facture_client, bv_chantier, bv_agence" },
    ],
  },

  /* ─────────────────────────────────────────────────────────────────────────────
     PILOTAGE CHANTIER — 37 KPIs
     ───────────────────────────────────────────────────────────────────────────── */
  {
    id: "pilotage-chantier",
    label: "Pilotage chantier",
    color: "#0EA5E9",
    kpis: [
      /* Synthèse financière */
      { nom: "Marché total",
        description: "Total du marché chantier signé, avenants inclus.",
        calcul: "Marché initial + somme des avenants signés sur le chantier.",
        source: "bv_marche" },
      { nom: "Marché initial",
        description: "Montant du marché à la signature, avant tout avenant ou travaux supplémentaires.",
        calcul: "Montant signé à la date de signature du marché initial.",
        source: "bv_marche" },
      { nom: "Travaux supplémentaires (TS) total",
        description: "Somme des avenants signés sur le chantier.",
        calcul: "Marché total − Marché initial.",
        source: "bv_marche" },
      { nom: "% TS / Marché initial",
        description: "Poids des travaux supplémentaires en pourcentage du marché initial.",
        calcul: "(TS total ÷ Marché initial) × 100.",
        source: "bv_marche" },
      { nom: "CA facturé chantier",
        description: "Total des montants facturés sur ce chantier depuis son démarrage.",
        calcul: "Somme des montants HT de toutes les factures liées au chantier.",
        source: "bv_facture_client" },
      { nom: "Avancement chantier %",
        description: "Avancement de la facturation par rapport au marché total.",
        calcul: "(CA facturé chantier ÷ Marché total) × 100. Méthode configurable.",
        source: "bv_facture_client, bv_marche, bv_kpi_rules (methode_avancement)" },
      { nom: "RAF chantier",
        description: "Reste à facturer pour ce chantier spécifique.",
        calcul: "Marché total − CA facturé chantier.",
        source: "bv_marche, bv_facture_client" },
      { nom: "Marge brute (€)",
        description: "Marge brute prévisionnelle = marché signé moins déboursé total chantier.",
        calcul: "Marché total − Total des déboursés budgétés (MO + fourniture + ST + matériel + divers).",
        source: "bv_marche, bv_chantier_budget" },
      { nom: "Taux marge brute (%)",
        description: "Taux de marge brute en pourcentage du marché total.",
        calcul: "(Marge brute ÷ Marché total) × 100.",
        source: "bv_marche, bv_chantier_budget" },
      { nom: "Marge nette (€)",
        description: "Marge nette prévisionnelle après frais généraux.",
        calcul: "Marge brute − frais généraux estimés (ratio paramétrable).",
        source: "bv_marche, bv_chantier_budget, bv_kpi_rules" },
      { nom: "Taux marge nette (%)",
        description: "Taux de marge nette en pourcentage du marché total.",
        calcul: "(Marge nette ÷ Marché total) × 100.",
        source: "bv_marche, bv_chantier_budget" },

      /* Budget par nature */
      { nom: "Budget déboursé MO",
        description: "Budget main d'œuvre initialement prévu sur le chantier.",
        calcul: "Montant du déboursé MO inscrit dans le budget chantier au démarrage.",
        source: "bv_chantier_budget" },
      { nom: "Budget déboursé fourniture",
        description: "Budget achats de matières et fournitures.",
        calcul: "Montant du déboursé fourniture dans le budget chantier.",
        source: "bv_chantier_budget" },
      { nom: "Budget déboursé sous-traitance",
        description: "Budget alloué aux prestations sous-traitées.",
        calcul: "Montant du déboursé ST dans le budget chantier.",
        source: "bv_chantier_budget" },
      { nom: "Budget déboursé matériel / location",
        description: "Budget pour location de matériel et engins.",
        calcul: "Montant du déboursé matériel/location dans le budget chantier.",
        source: "bv_chantier_budget" },
      { nom: "Budget déboursé divers",
        description: "Postes de coûts non classés dans les autres natures.",
        calcul: "Montant du déboursé divers dans le budget chantier.",
        source: "bv_chantier_budget" },
      { nom: "Budget total déboursé",
        description: "Somme de tous les déboursés budgétés du chantier.",
        calcul: "MO + fourniture + ST + matériel + divers du budget.",
        source: "bv_chantier_budget" },

      /* Budget à avancement */
      { nom: "Budget à avancement MO",
        description: "Quote-part du budget MO qui aurait dû être consommée à l'avancement actuel.",
        calcul: "Budget MO × Avancement %.",
        source: "bv_chantier_budget, bv_facture_client" },
      { nom: "Budget à avancement fourniture",
        description: "Quote-part du budget fourniture à l'avancement actuel.",
        calcul: "Budget fourniture × Avancement %.",
        source: "bv_chantier_budget, bv_facture_client" },
      { nom: "Budget à avancement ST",
        description: "Quote-part du budget sous-traitance à l'avancement actuel.",
        calcul: "Budget ST × Avancement %.",
        source: "bv_chantier_budget, bv_facture_client" },
      { nom: "Budget à avancement matériel",
        description: "Quote-part du budget matériel à l'avancement actuel.",
        calcul: "Budget matériel × Avancement %.",
        source: "bv_chantier_budget, bv_facture_client" },
      { nom: "Budget à avancement total",
        description: "Référentiel de dépense à l'avancement actuel, tous postes confondus.",
        calcul: "Budget total × Avancement %.",
        source: "bv_chantier_budget, bv_facture_client" },

      /* Réel par nature */
      { nom: "Réel MO",
        description: "Coût réel main d'œuvre dépensé à date (pointages valorisés).",
        calcul: "Somme des coûts MO calculés depuis les pointages (heures × coût horaire).",
        source: "bv_pointage_mo, bv_salarie" },
      { nom: "Réel fourniture",
        description: "Achats matériaux et fournitures réellement engagés sur le chantier.",
        calcul: "Somme des montants HT des achats fournisseurs liés au chantier.",
        source: "bv_achat_fournisseur, bv_achat_fournisseur_ligne" },
      { nom: "Réel sous-traitance",
        description: "Coûts sous-traitance réellement facturés sur le chantier.",
        calcul: "Somme des montants HT des prestations ST.",
        source: "bv_sous_traitance" },
      { nom: "Réel location matériel",
        description: "Coûts de location matériel réellement dépensés.",
        calcul: "Somme des achats catégorie 'location matériel'.",
        source: "bv_achat_fournisseur, bv_categorie_achat" },
      { nom: "Réel intérim",
        description: "Coût main d'œuvre intérimaire réellement dépensé.",
        calcul: "Somme des coûts pointage des salariés type_charge = 'interim'.",
        source: "bv_pointage_mo, bv_salarie" },
      { nom: "Réel stock",
        description: "Valeur des approvisionnements stock sortis sur le chantier.",
        calcul: "Somme des montants HT des mouvements stock affectés au chantier.",
        source: "bv_approvisionnement_chantier" },

      /* Écarts */
      { nom: "Écart MO",
        description: "Écart entre le réel MO et le budget à avancement MO.",
        calcul: "Réel MO − Budget à avancement MO. Négatif = sous-consommation favorable.",
        source: "Calculé depuis bv_pointage_mo et bv_chantier_budget" },
      { nom: "Écart fourniture",
        description: "Écart entre achats réels et budget fourniture à avancement.",
        calcul: "Réel fourniture − Budget à avancement fourniture.",
        source: "Calculé depuis bv_achat_fournisseur et bv_chantier_budget" },
      { nom: "Écart sous-traitance",
        description: "Écart entre coûts ST réels et budget ST à avancement.",
        calcul: "Réel ST − Budget à avancement ST.",
        source: "Calculé depuis bv_sous_traitance et bv_chantier_budget" },
      { nom: "Écart total",
        description: "Écart global entre toutes les dépenses réelles et le budget à avancement.",
        calcul: "Réel total − Budget à avancement total.",
        source: "Toutes les tables dépenses + bv_chantier_budget" },

      /* Engagements */
      { nom: "Engagé fournisseur",
        description: "Montant commandé chez les fournisseurs mais non encore facturé.",
        calcul: "Somme des commandes ouvertes (statut 'envoyée' ou 'en cours') non encore réceptionnées.",
        source: "bv_achat_fournisseur" },
      { nom: "Engagé sous-traitance",
        description: "Montant des marchés ST signés mais non encore facturés.",
        calcul: "Somme des contrats ST non encore facturés à 100%.",
        source: "bv_sous_traitance" },
      { nom: "Reste à dépenser",
        description: "Estimation du reste à débourser pour terminer le chantier.",
        calcul: "Budget total − Réel total − Engagé total. Doit rester positif.",
        source: "bv_chantier_budget, toutes les tables dépenses" },

      /* MO heures et coûts */
      { nom: "Heures totales chantier",
        description: "Total des heures pointées sur le chantier (travaillées + absences + intempéries).",
        calcul: "Somme des heures pointées toutes natures confondues.",
        source: "bv_pointage_mo" },
      { nom: "Heures travaillées",
        description: "Heures effectivement productives sur le chantier.",
        calcul: "Somme des heures pointées hors absences et intempéries.",
        source: "bv_pointage_mo" },
    ],
  },

  /* ─────────────────────────────────────────────────────────────────────────────
     DIRECTION — 20 KPIs
     ───────────────────────────────────────────────────────────────────────────── */
  {
    id: "direction",
    label: "Direction",
    color: "#8B5CF6",
    kpis: [
      { nom: "Nombre de chantiers en travaux",
        description: "Compteur des chantiers actuellement en phase travaux.",
        calcul: "Comptage des chantiers avec statut = 'travaux'.",
        source: "bv_chantier" },
      { nom: "Nombre de chantiers en étude",
        description: "Compteur des chantiers en phase étude / chiffrage.",
        calcul: "Comptage des chantiers avec statut = 'étude'.",
        source: "bv_chantier" },
      { nom: "Nombre de chantiers achevés",
        description: "Compteur des chantiers terminés (clôturés ou DGD validée).",
        calcul: "Comptage des chantiers avec statut = 'achevé'.",
        source: "bv_chantier" },
      { nom: "Marché total consolidé",
        description: "Somme de tous les marchés signés tous chantiers confondus.",
        calcul: "Somme des montants des marchés signés sur tous les chantiers actifs.",
        source: "bv_marche" },
      { nom: "CA YTD (Year-To-Date)",
        description: "Chiffre d'affaires facturé depuis le début de l'année jusqu'à aujourd'hui.",
        calcul: "Somme des montants HT des factures depuis le 1er janvier (ou début exercice).",
        source: "bv_facture_client, bv_kpi_rules (exercice_debut_mois)" },
      { nom: "RAF global",
        description: "Reste à facturer consolidé tous chantiers.",
        calcul: "Somme des RAF de chaque chantier (Marché − Facturé).",
        source: "bv_marche, bv_facture_client" },
      { nom: "Marge brute consolidée",
        description: "Marge brute prévisionnelle totale tous chantiers.",
        calcul: "Somme des marges brutes prévisionnelles de chaque chantier.",
        source: "bv_marche, bv_chantier_budget" },
      { nom: "Taux marge brute moyen",
        description: "Taux de marge brute moyen pondéré sur le portefeuille.",
        calcul: "(Marge brute totale ÷ Marché total) × 100.",
        source: "bv_marche, bv_chantier_budget" },
      { nom: "Nombre de chantiers en alerte",
        description: "Compteur des chantiers ayant au moins une alerte active.",
        calcul: "Comptage des chantier_id distincts présents dans les alertes actives.",
        source: "bv_alerte_chantier" },
      { nom: "Nombre d'alertes critiques",
        description: "Alertes de sévérité critique non encore résolues.",
        calcul: "Comptage des alertes avec severite = 'critique' et est_active = true.",
        source: "bv_alerte_chantier" },
      { nom: "Synthèse chantiers (tableau)",
        description: "Vue tabulaire de tous les chantiers avec leurs principaux KPIs.",
        calcul: "Jointure chantier × marchés × factures × budgets avec filtres optionnels.",
        source: "bv_chantier, bv_marche, bv_facture_client, bv_chantier_budget" },
      { nom: "Performance par conducteur",
        description: "Indicateurs de performance par conducteur de travaux.",
        calcul: "Agrégation par conducteur_id : nb chantiers, marché, facturé, taux marge, alertes.",
        source: "bv_chantier, bv_collaborateur, bv_marche, bv_facture_client" },
      { nom: "Performance par chargé d'affaires",
        description: "Indicateurs commerciaux par chargé d'affaires.",
        calcul: "Agrégation par charge_affaires_id : nb chantiers, marché, facturé, taux marge.",
        source: "bv_chantier, bv_collaborateur, bv_marche, bv_facture_client" },
      { nom: "Performance par activité",
        description: "CA et marché par activité métier (Électricité, Plomberie…).",
        calcul: "Agrégation par activite_id : nb chantiers, marché signé, facturé.",
        source: "bv_chantier, bv_activite, bv_marche, bv_facture_client" },
      { nom: "Performance par agence",
        description: "CA et marché par agence géographique.",
        calcul: "Agrégation par agence_id : nb chantiers, marché signé, facturé.",
        source: "bv_chantier, bv_agence, bv_marche, bv_facture_client" },
      { nom: "Chantiers en dérive",
        description: "Chantiers où les dépenses réelles dépassent le budget à avancement.",
        calcul: "Chantiers où (Réel total > Budget à avancement total). Écart positif = dérive.",
        source: "bv_pointage_mo, bv_achat_fournisseur, bv_sous_traitance, bv_chantier_budget" },
      { nom: "Chantiers en marge négative",
        description: "Chantiers dont la marge brute projetée est passée sous zéro.",
        calcul: "Chantiers où (Marché total − Dépenses estimées à terminaison) < 0.",
        source: "bv_marche, bv_chantier_projection" },
      { nom: "Donut marché par activité",
        description: "Répartition du marché total par activité métier.",
        calcul: "Marché total agrégé par activite_id, présenté en donut.",
        source: "bv_marche, bv_chantier, bv_activite" },
      { nom: "Donut dépenses par nature",
        description: "Répartition des dépenses YTD par nature (MO, fourniture, ST…).",
        calcul: "Somme des dépenses réelles par nature, présentée en donut.",
        source: "bv_pointage_mo, bv_achat_fournisseur, bv_sous_traitance, bv_approvisionnement_chantier" },
      { nom: "Courbe marge brute mensuelle",
        description: "Évolution mensuelle de la marge brute réalisée sur l'année.",
        calcul: "Pour chaque mois : CA facturé − dépenses réelles du mois.",
        source: "bv_facture_client, bv_pointage_mo, bv_achat_fournisseur, bv_sous_traitance" },
    ],
  },

  /* ─────────────────────────────────────────────────────────────────────────────
     COMMERCIAL — 17 KPIs
     ───────────────────────────────────────────────────────────────────────────── */
  {
    id: "commercial",
    label: "Commercial",
    color: "#3B82F6",
    kpis: [
      { nom: "Chiffrages en cours",
        description: "Devis actuellement à l'étude ou en attente de réponse client.",
        calcul: "Comptage des chiffrages avec statut ∈ {en_cours, envoyé}.",
        source: "bv_chiffrage" },
      { nom: "Montant portefeuille",
        description: "Total HT des chiffrages encore en jeu (non perdus, non transformés).",
        calcul: "Somme des montants HT des chiffrages avec statut ∈ {en_cours, envoyé}.",
        source: "bv_chiffrage" },
      { nom: "Taux de transformation",
        description: "Pourcentage de chiffrages acceptés sur le total chiffré.",
        calcul: "(Chiffrages acceptés ÷ Total chiffrages émis sur la période) × 100.",
        source: "bv_chiffrage" },
      { nom: "Marchés signés YTD",
        description: "Total des marchés signés depuis le début de l'année.",
        calcul: "Somme des montants des marchés signés sur l'exercice en cours.",
        source: "bv_marche" },
      { nom: "Marge nette moyenne",
        description: "Marge nette moyenne prévue sur les chiffrages en cours.",
        calcul: "Moyenne des taux_marge_nette des chiffrages actifs, pondérée par leur montant.",
        source: "bv_chiffrage" },
      { nom: "Devis en attente de réponse",
        description: "Chiffrages envoyés au client mais sans retour.",
        calcul: "Comptage des chiffrages avec statut = 'envoyé'.",
        source: "bv_chiffrage" },
      { nom: "Délai moyen de réponse",
        description: "Délai moyen entre envoi du devis et acceptation par le client.",
        calcul: "Moyenne des (date_acceptation − date_envoi) en jours pour les chiffrages acceptés.",
        source: "bv_chiffrage" },
      { nom: "Pipeline (liste détaillée)",
        description: "Tableau des chiffrages actifs avec montant, marge, client.",
        calcul: "Listing des chiffrages statut ∈ {en_cours, envoyé, accepté} avec marges calculées.",
        source: "bv_chiffrage, bv_chantier, bv_tiers" },
      { nom: "Chiffrages perdus",
        description: "Devis refusés ou sans suite sur la période.",
        calcul: "Listing des chiffrages statut ∈ {refusé, sans_suite}.",
        source: "bv_chiffrage" },
      { nom: "Portefeuille par client",
        description: "Concentration du portefeuille commercial par client.",
        calcul: "Agrégation des chiffrages actifs par client_id : nombre + montant total.",
        source: "bv_chiffrage, bv_tiers" },
      { nom: "Transformation par mois",
        description: "Évolution mensuelle du taux de transformation chiffrages → marchés.",
        calcul: "Pour chaque mois : nb chiffrages acceptés ÷ nb chiffrages total émis.",
        source: "bv_chiffrage" },
      { nom: "Marchés signés récents",
        description: "Liste des derniers marchés signés avec leurs marges initiales.",
        calcul: "Top N des marchés par date de signature décroissante.",
        source: "bv_marche, bv_chantier, bv_tiers" },
      { nom: "Marché initial vs TS",
        description: "Comparaison marché initial vs avenants par chantier.",
        calcul: "Pour chaque chantier : marché initial, somme TS, % TS / initial.",
        source: "bv_marche" },
      { nom: "Marge chiffrage (par devis)",
        description: "Marge prévue dans le devis initial avant signature.",
        calcul: "(Montant chiffrage − Total déboursé prévu) ÷ Montant chiffrage.",
        source: "bv_chiffrage" },
      { nom: "Marge marché (à la signature)",
        description: "Marge réelle figée à la signature du marché.",
        calcul: "(Montant marché − Total déboursé contractuel) ÷ Montant marché.",
        source: "bv_marche" },
      { nom: "Écart marge chiffrage / marché",
        description: "Dégradation ou amélioration de la marge entre chiffrage et signature.",
        calcul: "Marge marché − Marge chiffrage. Négatif = dégradation à la signature.",
        source: "bv_chiffrage, bv_marche" },
      { nom: "Performance par chargé d'affaires",
        description: "Mêmes indicateurs commerciaux, filtrés par chargé d'affaires.",
        calcul: "Agrégation par charge_affaires_id de tous les KPIs commerciaux.",
        source: "bv_chiffrage, bv_marche, bv_collaborateur" },
    ],
  },

  /* ─────────────────────────────────────────────────────────────────────────────
     RH / MAIN D'ŒUVRE — 23 KPIs
     ───────────────────────────────────────────────────────────────────────────── */
  {
    id: "rh",
    label: "RH / Main d'œuvre",
    color: "#EC4899",
    kpis: [
      { nom: "Effectif total",
        description: "Nombre total de salariés actifs sur la période.",
        calcul: "Comptage des salariés ayant au moins un pointage sur la période.",
        source: "bv_salarie, bv_pointage_mo" },
      { nom: "Effectif interne",
        description: "Salariés permanents en CDI/CDD propres à l'entreprise.",
        calcul: "Comptage des salariés type_charge = 'interne'.",
        source: "bv_salarie" },
      { nom: "Effectif intérim",
        description: "Travailleurs intérimaires actifs sur la période.",
        calcul: "Comptage des salariés type_charge = 'interim'.",
        source: "bv_salarie" },
      { nom: "Effectif prêté",
        description: "Salariés détachés ou prêtés depuis une autre entité.",
        calcul: "Comptage des salariés type_charge = 'externe' ou 'prêté'.",
        source: "bv_salarie" },
      { nom: "Ratio intérim",
        description: "Part du personnel intérimaire dans l'effectif total.",
        calcul: "(Effectif intérim ÷ Effectif total) × 100.",
        source: "bv_salarie" },
      { nom: "Heures pointées",
        description: "Total des heures pointées sur la période (toutes natures).",
        calcul: "Somme des heures de pointage (travaillées + absence + intempéries).",
        source: "bv_pointage_mo" },
      { nom: "Heures travaillées",
        description: "Heures productives uniquement, hors absences et intempéries.",
        calcul: "Somme des heures où type_heure = 'travaillée'.",
        source: "bv_pointage_mo" },
      { nom: "Heures d'absence",
        description: "Heures pointées en absence (congés, maladie, etc.).",
        calcul: "Somme des heures où type_heure = 'absence'.",
        source: "bv_pointage_mo" },
      { nom: "Heures intempéries",
        description: "Heures perdues pour cause d'intempéries.",
        calcul: "Somme des heures où type_heure = 'intempérie'.",
        source: "bv_pointage_mo" },
      { nom: "Coût MO total",
        description: "Coût total main d'œuvre sur la période (déboursé).",
        calcul: "Somme des heures travaillées × coût horaire déboursé du salarié.",
        source: "bv_pointage_mo, bv_salarie" },
      { nom: "Coût MO de revient",
        description: "Coût total main d'œuvre charges patronales incluses.",
        calcul: "Coût MO × coefficient de revient (déboursé → revient).",
        source: "bv_pointage_mo, bv_salarie, bv_kpi_rules" },
      { nom: "Coût horaire moyen déboursé",
        description: "Coût horaire moyen pondéré sur les heures travaillées (déboursé).",
        calcul: "Coût MO total ÷ Heures travaillées.",
        source: "bv_pointage_mo, bv_salarie" },
      { nom: "Coût horaire moyen de revient",
        description: "Coût horaire moyen pondéré incluant les charges patronales.",
        calcul: "Coût MO revient ÷ Heures travaillées.",
        source: "bv_pointage_mo, bv_salarie" },
      { nom: "Taux d'absence",
        description: "Part des heures d'absence dans le total pointé.",
        calcul: "(Heures absence ÷ Heures pointées) × 100.",
        source: "bv_pointage_mo" },
      { nom: "Taux d'intempéries",
        description: "Part des heures intempéries dans le total pointé.",
        calcul: "(Heures intempéries ÷ Heures pointées) × 100.",
        source: "bv_pointage_mo" },
      { nom: "Heures par fonction",
        description: "Répartition des heures et coûts par fonction (compagnon, chef d'équipe…).",
        calcul: "Agrégation des heures + coûts groupée par fonction du salarié.",
        source: "bv_pointage_mo, bv_salarie" },
      { nom: "Heures par type de charge",
        description: "Répartition des heures et coûts par type (interne, intérim, externe).",
        calcul: "Agrégation des heures + coûts par type_charge.",
        source: "bv_pointage_mo, bv_salarie" },
      { nom: "Heures par chantier",
        description: "Heures pointées par chantier (productivité chantier).",
        calcul: "Agrégation par chantier_id : travaillées, absence, intempéries, total, coût.",
        source: "bv_pointage_mo, bv_chantier" },
      { nom: "Heures par salarié (détail)",
        description: "Détail individuel par salarié sur la période.",
        calcul: "Listing salarié × heures travaillées/absence/intempéries × coût déboursé.",
        source: "bv_pointage_mo, bv_salarie" },
      { nom: "Intérim par chantier",
        description: "Heures et coût intérim affectés par chantier.",
        calcul: "Filtrage type_charge = 'interim', agrégation par chantier.",
        source: "bv_pointage_mo, bv_salarie, bv_chantier" },
      { nom: "Évolution mensuelle heures",
        description: "Volume horaire pointé chaque mois sur l'année.",
        calcul: "Agrégation des heures travaillées par mois.",
        source: "bv_pointage_mo" },
      { nom: "Évolution mensuelle coût MO",
        description: "Coût main d'œuvre déboursé mois par mois.",
        calcul: "Agrégation du coût MO par mois sur l'année.",
        source: "bv_pointage_mo, bv_salarie" },
      { nom: "Évolution interne vs intérim",
        description: "Mix mensuel interne/intérim en heures travaillées.",
        calcul: "Pour chaque mois : heures internes vs heures intérim, présenté en barres empilées.",
        source: "bv_pointage_mo, bv_salarie" },
    ],
  },

  /* ─────────────────────────────────────────────────────────────────────────────
     ALERTES — 20 KPIs
     ───────────────────────────────────────────────────────────────────────────── */
  {
    id: "alertes",
    label: "Alertes",
    color: "#DC2626",
    kpis: [
      { nom: "Alertes ouvertes",
        description: "Nombre total d'alertes actives toutes sévérités confondues.",
        calcul: "Comptage des alertes avec est_active = true.",
        source: "bv_alerte_chantier" },
      { nom: "Alertes critiques",
        description: "Alertes de sévérité critique actives (action immédiate requise).",
        calcul: "Comptage des alertes severite = 'critique' et est_active = true.",
        source: "bv_alerte_chantier" },
      { nom: "Alertes warning",
        description: "Alertes de sévérité warning actives (à surveiller).",
        calcul: "Comptage des alertes severite = 'warning' et est_active = true.",
        source: "bv_alerte_chantier" },
      { nom: "Alertes info",
        description: "Alertes de sévérité info actives (information).",
        calcul: "Comptage des alertes severite = 'info' et est_active = true.",
        source: "bv_alerte_chantier" },
      { nom: "Délai moyen de résolution",
        description: "Temps moyen entre déclenchement et clôture d'une alerte.",
        calcul: "Moyenne en jours de (date_resolution − date_declenchement) sur l'historique.",
        source: "bv_alerte_chantier" },
      { nom: "Liste des alertes actives",
        description: "Tableau détaillé des alertes en cours (chantier, type, message, ancienneté).",
        calcul: "Listing des alertes est_active = true, triées par date de déclenchement décroissante.",
        source: "bv_alerte_chantier, bv_chantier, bv_collaborateur" },
      { nom: "Alertes par type",
        description: "Répartition des alertes actives par type (MO dépassée, marge faible…).",
        calcul: "Agrégation par type_alerte : nb total, nb critiques, nb warnings.",
        source: "bv_alerte_chantier" },
      { nom: "Alertes par conducteur",
        description: "Volume d'alertes attribuées à chaque conducteur de travaux.",
        calcul: "Agrégation par conducteur du chantier : nb alertes, nb critiques, nb chantiers.",
        source: "bv_alerte_chantier, bv_chantier, bv_collaborateur" },
      { nom: "Historique des résolutions",
        description: "20 dernières alertes résolues avec durée de traitement.",
        calcul: "Listing des alertes est_active = false, triées par date_resolution décroissante.",
        source: "bv_alerte_chantier" },
      { nom: "Détection : MO dépassée",
        description: "Chantiers où le réel MO dépasse le budget à avancement de plus que le seuil.",
        calcul: "Réel MO > Budget à avancement MO × (1 + seuil_mo_depassement%).",
        source: "bv_pointage_mo, bv_chantier_budget, bv_kpi_rules (seuil_mo_depassement)" },
      { nom: "Détection : marge faible",
        description: "Chantiers où la marge brute projetée est inférieure au seuil paramétrable.",
        calcul: "Taux marge brute < seuil_marge_alerte (paramétrable, défaut 15%).",
        source: "bv_marche, bv_chantier_budget, bv_kpi_rules (seuil_marge_alerte)" },
      { nom: "Détection : intérim excessif",
        description: "Chantiers où le ratio intérim dépasse une part jugée excessive.",
        calcul: "(Heures intérim ÷ Heures totales chantier) × 100 > seuil paramétrable.",
        source: "bv_pointage_mo, bv_salarie, bv_kpi_rules" },
      { nom: "Détection : sans facturation > 90j",
        description: "Chantiers actifs sans facture émise depuis plus de 90 jours.",
        calcul: "Chantiers statut = 'travaux' avec MAX(date_piece factures) > 90 jours.",
        source: "bv_chantier, bv_facture_client" },
      { nom: "Détection : écart chiffrage/marché",
        description: "Dégradation de marge entre devis et signature.",
        calcul: "Marge marché − Marge chiffrage < 0 (dégradation à la signature).",
        source: "bv_chiffrage, bv_marche" },
      { nom: "Détection : sous-facturation",
        description: "Chantiers où la facturation est en retard sur l'avancement réel.",
        calcul: "Avancement travaux − Avancement facturation > seuil_sous_facturation (points).",
        source: "bv_chantier_projection, bv_facture_client, bv_kpi_rules" },
      { nom: "Détection : BL non facturés",
        description: "Bons de livraison reçus mais non encore inclus dans une situation facturée.",
        calcul: "Achats fournisseurs réceptionnés sans rattachement à une facture client postérieure.",
        source: "bv_achat_fournisseur, bv_facture_client" },
      { nom: "Détection : avenant non signé",
        description: "TS exécutés mais non encore formalisés en avenant signé.",
        calcul: "Dépenses au-delà du marché initial sans avenant correspondant signé.",
        source: "bv_marche, bv_chantier_budget" },
      { nom: "Ancienneté des alertes",
        description: "Nombre de jours depuis le déclenchement de chaque alerte active.",
        calcul: "Date du jour − date_declenchement de l'alerte.",
        source: "bv_alerte_chantier" },
      { nom: "Seuils configurés (lecture)",
        description: "Affichage des seuils paramétrables actuellement actifs.",
        calcul: "Lecture directe des règles seuil_* dans la table de configuration.",
        source: "bv_kpi_rules (codes seuil_*)" },
      { nom: "Filtres alertes",
        description: "Filtrage des alertes par sévérité, type, conducteur, inclure résolues.",
        calcul: "Application des filtres p_severite, p_type_alerte, p_conducteur_id, p_actives_seulement.",
        source: "bv_alerte_chantier" },
    ],
  },
];

/* ═══════════════════════════════════════════════════════════════════════════════
   PAGE AIDE — 135 KPIs documentés
   ═══════════════════════════════════════════════════════════════════════════════ */
export default function PageAide() {
  const [activeTab, setActiveTab] = useState("ca");
  const [openId, setOpenId] = useState(null);
  const [search, setSearch] = useState("");

  /* ─── Compteur total ─── */
  const totalCount = useMemo(
    () => DASHBOARDS.reduce((acc, d) => acc + d.kpis.length, 0),
    []
  );

  /* ─── Indexation pour recherche ─── */
  const searchResults = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return null;
    return DASHBOARDS
      .map((d) => ({
        ...d,
        kpis: d.kpis.filter((k) =>
          k.nom.toLowerCase().includes(q) ||
          k.description.toLowerCase().includes(q) ||
          k.calcul.toLowerCase().includes(q) ||
          k.source.toLowerCase().includes(q)
        ),
      }))
      .filter((d) => d.kpis.length > 0);
  }, [search]);

  /* ─── Reset état ouvert quand on change d'onglet ou de recherche ─── */
  const lastModeRef = useRef("tab");
  useEffect(() => {
    const mode = search ? "search" : "tab";
    if (mode !== lastModeRef.current) {
      setOpenId(null);
      lastModeRef.current = mode;
    }
  }, [search]);

  const toggle = (key) => setOpenId((prev) => (prev === key ? null : key));

  const activeDashboard = DASHBOARDS.find((d) => d.id === activeTab) || DASHBOARDS[0];

  /* ─── Rendu d'une carte KPI ─── */
  const renderKpiCard = (dashboard, kpi, idx) => {
    const key = `${dashboard.id}-${idx}`;
    const isOpen = openId === key;
    return (
      <div
        key={key}
        style={{
          background: C.surface, borderRadius: 12, border: `1px solid ${C.border}`,
          borderLeft: `4px solid ${dashboard.color}`, overflow: "hidden",
          boxShadow: isOpen ? "0 2px 12px rgba(0,0,0,0.08)" : "0 1px 4px rgba(0,0,0,0.03)",
          transition: "box-shadow 150ms",
        }}
      >
        {/* En-tête cliquable */}
        <div
          onClick={() => toggle(key)}
          style={{
            padding: "14px 18px", cursor: "pointer",
            display: "flex", alignItems: "center", gap: 14,
          }}
        >
          <div style={{
            width: 28, height: 28, borderRadius: 8, background: `${dashboard.color}15`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 11, fontWeight: 800, color: dashboard.color, fontFamily: mono, flexShrink: 0,
          }}>
            {idx + 1}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{kpi.nom}</div>
            <div style={{
              fontSize: 12, color: C.slate, marginTop: 2,
              overflow: "hidden", textOverflow: "ellipsis",
              display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical",
            }}>
              {kpi.description}
            </div>
          </div>
          <div style={{
            fontSize: 14, color: C.slateLight, transition: "transform 200ms",
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
          }}>▾</div>
        </div>

        {/* Détail */}
        {isOpen && (
          <div style={{
            padding: "0 18px 18px 60px",
            animation: "fadeIn 200ms ease-out",
          }}>
            <style>{`@keyframes fadeIn{from{opacity:0;transform:translateY(-8px)}to{opacity:1;transform:translateY(0)}}`}</style>

            <div style={{ display: "grid", gridTemplateColumns: "110px 1fr", gap: "8px 16px", fontSize: 12 }}>
              <div style={{ fontWeight: 600, color: C.slate }}>Description</div>
              <div style={{ color: C.text, lineHeight: 1.5 }}>{kpi.description}</div>

              <div style={{ fontWeight: 600, color: C.slate }}>Calcul</div>
              <div style={{ color: C.text, lineHeight: 1.5 }}>{kpi.calcul}</div>

              <div style={{ fontWeight: 600, color: C.slate }}>Source</div>
              <div style={{
                color: C.text, fontFamily: mono, fontSize: 11,
                background: C.bg, padding: "6px 10px", borderRadius: 6,
              }}>
                {kpi.source}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      {/* ─── En-tête ─── */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: C.text }}>
            Aide & KPIs
          </h1>
          <span style={{
            padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700,
            background: C.navy, color: "#fff",
          }}>
            {totalCount} KPIs
          </span>
        </div>
        <p style={{ margin: "4px 0 0", fontSize: 13, color: C.slate }}>
          Documentation des indicateurs — description, formule de calcul et source de données par dashboard
        </p>
      </div>

      {/* ─── Barre de recherche ─── */}
      <div style={{ position: "relative", marginBottom: 20 }}>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher un KPI (nom, description, calcul, table)…"
          style={{
            width: "100%", padding: "10px 38px 10px 14px",
            border: `1px solid ${search ? C.accent : C.border}`,
            borderRadius: 10, fontSize: 13, fontFamily: font,
            background: C.surface, color: C.text, outline: "none",
            boxSizing: "border-box", transition: "border 150ms",
          }}
        />
        {search && (
          <button
            onClick={() => setSearch("")}
            style={{
              position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
              background: C.bg, border: "none", borderRadius: "50%",
              width: 22, height: 22, cursor: "pointer",
              color: C.slate, fontSize: 14, lineHeight: 1,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
            aria-label="Effacer la recherche"
          >
            ×
          </button>
        )}
      </div>

      {/* ─── MODE RECHERCHE : résultats groupés par dashboard ─── */}
      {searchResults !== null ? (
        <>
          <div style={{ marginBottom: 16, fontSize: 12, color: C.slate }}>
            {searchResults.length === 0 ? (
              <>Aucun KPI ne correspond à <strong style={{ color: C.text }}>« {search} »</strong>.</>
            ) : (
              <>
                <strong style={{ color: C.text }}>
                  {searchResults.reduce((a, d) => a + d.kpis.length, 0)}
                </strong>
                {" résultat"}
                {searchResults.reduce((a, d) => a + d.kpis.length, 0) > 1 ? "s" : ""}
                {" pour "}
                <strong style={{ color: C.text }}>« {search} »</strong>
              </>
            )}
          </div>

          {searchResults.map((d) => (
            <div key={d.id} style={{ marginBottom: 24 }}>
              <div style={{
                fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px",
                color: d.color, marginBottom: 10, paddingBottom: 6,
                borderBottom: `2px solid ${d.color}30`,
                display: "flex", alignItems: "center", gap: 8,
              }}>
                {d.label}
                <span style={{
                  padding: "1px 8px", borderRadius: 999, fontSize: 10, fontWeight: 700,
                  background: `${d.color}20`, color: d.color,
                }}>
                  {d.kpis.length}
                </span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {d.kpis.map((k, i) => renderKpiCard(d, k, i))}
              </div>
            </div>
          ))}
        </>
      ) : (
        /* ─── MODE ONGLETS ─── */
        <>
          <div style={{
            display: "flex", gap: 4, marginBottom: 20, flexWrap: "wrap",
            borderBottom: `2px solid ${C.border}`, paddingBottom: 0,
          }}>
            {DASHBOARDS.map((d) => {
              const active = activeTab === d.id;
              return (
                <button
                  key={d.id}
                  onClick={() => { setActiveTab(d.id); setOpenId(null); }}
                  style={{
                    padding: "10px 18px", border: "none", cursor: "pointer",
                    fontSize: 13, fontWeight: active ? 700 : 500,
                    color: active ? d.color : C.slate,
                    background: active ? `${d.color}10` : "transparent",
                    borderBottom: active ? `3px solid ${d.color}` : "3px solid transparent",
                    borderRadius: "8px 8px 0 0",
                    transition: "all 150ms",
                    fontFamily: font,
                  }}
                >
                  {d.label}
                  <span style={{
                    marginLeft: 8, fontSize: 10, fontWeight: 700,
                    background: active ? d.color : C.slateLight,
                    color: "#fff", padding: "2px 7px", borderRadius: 99,
                  }}>
                    {d.kpis.length}
                  </span>
                </button>
              );
            })}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {activeDashboard.kpis.map((k, i) => renderKpiCard(activeDashboard, k, i))}
          </div>
        </>
      )}
    </div>
  );
}
